import React, { useState } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useIsMobile, useSurveyContent } from "../../../../shared/hooks";
import Alert from "../../../_shared/alert";
import {
  useHandleSubmit,
  usePaymentSelectors,
  useRedirectToUrl,
} from "./hooks";
import { surveyPortalMixpanel } from "../../../../vendor/mixpanel";

interface FormInputs {
  email: string;
  confirmEmail: string;
}

const GENERIC_ERROR_MESSAGE = (
  <>
    Please try again. If you need help, reach out to us at{" "}
    <a href="mailto:payments@newtonx.com">payments@newtonx.com</a>.
  </>
);

const PaymentClaim = () => {
  const { handleSubmit, control, watch } = useForm<FormInputs>();

  const emailValue = watch("email");
  const isMobile = useIsMobile();
  const [alertDismissed, setAlertDismissed] = useState(false);
  const { surveyContent } = useSurveyContent();
  const seid = surveyContent?.subject ?? "";
  const surveyId = surveyContent?.survey_id ?? 0;
  const source = surveyContent?.survey_host ?? "";
  const compensation = surveyContent?.content?.survey_compensation ?? 0;
  const postPayment = useHandleSubmit();
  const [loading, setLoading] = useState(false);
  const { alert_level, alert_msg, redirect_to } = usePaymentSelectors();
  const urlAux = window.location.href.split("/c/");
  const basePaymentPendingUrl = urlAux[0] + "/payment_pending";
  const paymentPendingUrl = new URL(basePaymentPendingUrl);

  paymentPendingUrl.searchParams.append("survey_template", "SURVEY_2025");
  paymentPendingUrl.searchParams.append("seid", seid);
  if (emailValue?.trim()) {
    paymentPendingUrl.searchParams.append("email_address", emailValue);
  }
  paymentPendingUrl.searchParams.append("survey_host", source);
  paymentPendingUrl.searchParams.append("survey_id", surveyId.toString());
  paymentPendingUrl.searchParams.append("survey_url", window.location.href);

  useRedirectToUrl(paymentPendingUrl.toString());

  const showAlert = !!alert_level && !redirect_to && !alertDismissed;
  const alertSeverity = alert_level === "success" ? "info" : "error";
  const alertMessage = alert_msg || GENERIC_ERROR_MESSAGE;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    setAlertDismissed(false);
    setLoading(true);
    try {
      const formattedData = {
        request_url: window.location.href,
        email: data.email,
      };
      await postPayment(formattedData);
    } finally {
      setLoading(false);
    }
  };

  const hasTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasTrackedRef.current && surveyContent?.subject) {
      surveyPortalMixpanel.track("Payment Details Page Viewed", {
        SSEID: surveyContent?.subject,
        survey_id: surveyContent?.survey_id,
        survey_host: surveyContent?.survey_host,
      });
      hasTrackedRef.current = true;
    }
  }, [surveyContent]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: showAlert ? (!isMobile ? "-48px" : "-16px") : 0,
      }}
    >
      {showAlert && (
        <Alert
          severity={alertSeverity}
          open={showAlert}
          onClose={() => setAlertDismissed(true)}
          title={alertSeverity === "error" ? "Error submitting payment" : ""}
          message={alertMessage}
        />
      )}
      <Box maxWidth="480px" component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography variant="h2" marginBottom="16px" fontWeight={700}>
          Enter your payment email
        </Typography>

        <Typography variant="body1" color="text.secondary" marginBottom="16px">
          Thanks for completing the survey! Let us know where to email your $
          {compensation} payment.
        </Typography>

        {/* Email */}
        <Controller
          name="email"
          control={control}
          rules={{
            required: "Required field",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, // basic email regex
              message: "Please enter a valid email address",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Email address*"
              sx={{ marginBottom: "16px" }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />

        {/* Confirm Email */}
        <Controller
          name="confirmEmail"
          control={control}
          rules={{
            required: "Required field",
            validate: (value) =>
              value === emailValue || "Email addresses must match",
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              label="Confirm email address*"
              sx={{ marginBottom: "16px" }}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: "16px" }}
          color="primary"
          disabled={loading}
          type="submit"
        >
          SUBMIT
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentClaim;
